import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, forkJoin, Subject } from 'rxjs';
import { MessageService } from 'primeng/api';
import { OrderService } from '../../../services/order.service';
import { AccountService } from '../../../services/account.service';
import { AuthService } from '../../../services/auth.service';
import { UserService } from '../../../services/user.service';
import { ProductService } from '../../../services/product.service';
import { ColumnPreferenceService } from '../../../services/column-preference-service.service';
import { ExportExcelService } from '../../../services/export-excel.service';
import {
  Order, OrderItem, OrderLocation, OrderMovement, OrderSearchParams, OrderStatus, OrderType, PLATFORM_LOGOS,
  PLATFORM_OPTIONS, StockViolation
} from '../../../domain/order.model';
import { Account, PageableAccount } from '../../../domain/account.model';
import { AccountDropdown } from '../../../domain/accountDropdown.model';
import { ColumnConfig } from '../../../domain/column_config.model';
import { User } from '../../../domain/user.model';
import { RoleConstants } from '../../../domain/role.model';
import { Product, ProductSearchParams } from '../../../domain/product.model';

const ALL_ACCOUNTS_OPTION = { accountId: null, name: 'Todas las cuentas' };

// Working copy of an order line while Productos is being edited — same shape as the
// create-wizard's cart item, kept local since that one isn't exported.
interface OrderItemDraft {
  productId: number;
  sku?: string;
  description?: string;
  variant?: string;
  uom?: string;
  availableQuantity: number;
  quantity: number;
  comments: string;
  insufficientStock?: boolean;
}

@Component({
  selector: 'app-orders',
  standalone: false,
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss',
  providers: [MessageService]
})
export class OrdersComponent implements OnInit {
  orders: Order[] = [];
  accounts: any[] = [];
  userAccount: Account = {};
  assignableUsers: User[] = [];
  totalRecords: number = 0;
  loading: boolean = true;
  accountLoading: boolean = false;
  exportLoading: boolean = false;

  statusOptions = [
    { value: OrderStatus.CREADA, label: 'Creada' },
    { value: OrderStatus.EN_PROCESO, label: 'En Proceso' },
    { value: OrderStatus.COMPLETADA, label: 'Completada' },
    { value: OrderStatus.CANCELADA, label: 'Cancelada' },
  ];

  orderTypeOptions = [
    { value: OrderType.ENTREGA, label: 'Entrega' },
    { value: OrderType.RECEPCION, label: 'Recepción' },
  ];

  verifiedOptions = [
    { value: true, label: 'Verificada' },
    { value: false, label: 'Pendiente' },
  ];

  platformOptions = PLATFORM_OPTIONS;
  locationOptions = [
    { label: 'Domicilio', value: OrderLocation.DOMICILIO },
    { label: 'Bodega', value: OrderLocation.BODEGA },
  ];

  filterForm: FormGroup = this.fb.group({
    accountId: [null],
  });
  searchAccountSubject = new Subject<string>();
  searchAccountQuery: string = '';
  orderIdFilterText: string = '';

  // Abreviatura usada al armar el código de orden (ej. ENT-1045), para identificar de un
  // vistazo el tipo sin entrar al detalle — útil al compartirlo por WhatsApp. orderType no es
  // editable una vez creada la orden (no está en OrderUpdateDTO), así que el código es estable.
  private readonly ORDER_TYPE_ABBREVIATIONS: Record<string, string> = {
    [OrderType.ENTREGA]: 'ENT',
    [OrderType.RECEPCION]: 'REC',
  };

  tableRows: number = 30;
  firstPage = 0;
  lastLazyEvent: any = null;

  ALL_COLUMNS: ColumnConfig[] = [
    { field: 'rowIndex', header: '#', type: 'number', alwaysVisible: true },
    { field: 'orderId', header: 'ID', type: 'number', sortable: true },
    { field: 'account.name', header: 'Cuenta', type: 'string', sortable: true },
    { field: 'orderType', header: 'Tipo de Orden', type: 'tag', sortable: true },
    { field: 'platform', header: 'Plataforma', type: 'string', sortable: true },
    { field: 'productCount', header: 'Productos', type: 'number', sortable: true },
    { field: 'totalArticles', header: 'Artículos', type: 'number', sortable: true },
    { field: 'assignedUser.name', header: 'Asignado', type: 'string', sortable: true },
    { field: 'status', header: 'Estado', type: 'tag', sortable: true },
    { field: 'creationDate', header: 'Creada', type: 'date', sortable: true },
    { field: 'updateDate', header: 'Actualizada', type: 'date', sortable: true },
    { field: 'scheduledDate', header: 'Fecha de Orden', type: 'date', sortable: true },
    { field: 'verified', header: 'Verificada', type: 'tag', sortable: true },
    { field: 'completedDate', header: 'Completada', type: 'date', sortable: true },
    { field: 'actions', header: 'Acciones', type: 'actions', alwaysVisible: true },
  ];
  columnsToggleList: ColumnConfig[] = [];
  appliedTableColumns: ColumnConfig[] = [];
  columnSearchTerm: string = '';
  selectAll: boolean = false;

  currentRole: string;
  currentUserId: string;

  //--- View details dialog (info + items + history + inline edit) ---
  viewDialogVisible = false;
  viewLoading = false;
  viewingOrder: Order | null = null;
  movements: OrderMovement[] = [];

  // Jira-style inline editing: only one field editable at a time, staged in viewForm until
  // confirmed (pencil → check), then all staged changes save together with one mandatory
  // comment. Asignado is editable too, but only for BILD_* roles — see canEditAssignee.
  editingField: string | null = null;
  viewForm: FormGroup = this.fb.group({
    description: [''],
    scheduledDate: [null],
    titular: [''],
    location: [null],
    address: [''],
    platform: [null],
    assignedUserId: [null],
  });
  viewOriginal: Record<string, any> = {};
  saveComment = '';
  savingChanges = false;
  savingAssignment = false;
  savingField = false;
  detailsExpanded = true;

  // Cancelar orden: row-level action (Acciones column), gated by canCancelOrder(order).
  cancelDialogVisible = false;
  cancelingOrder: Order | null = null;
  cancelComment = '';
  cancelingOrderLoading = false;

  // Productos follows the same click-to-edit gate (canEditFields) as the other fields, but
  // since it's a list rather than a single control it can't live in viewForm — it's staged
  // here instead and folded into the same immediate-save-at-CREADA / batched-comment-flow
  // split the rest of the fields use (see confirmEditItems/saveItemsImmediate/itemsChanged).
  editingItems = false;
  itemsDraft: OrderItemDraft[] = [];
  originalItems: OrderItemDraft[] = [];
  itemsStockWarning: string | null = null;
  savingItems = false;
  activeItemCommentItem: OrderItemDraft | null = null;
  activeViewCommentItem: OrderItem | null = null;

  itemsCatalogProducts: Product[] = [];
  itemsCatalogTotalRecords = 0;
  itemsCatalogLoading = false;
  itemsCatalogRows = 10;
  itemsCatalogSearchTerm = '';
  itemsCatalogSearchSubject = new Subject<string>();
  lastItemsCatalogEvent: any = null;

  //--- Activity: Comentarios (Jira-style rich text notes) + Historial (field changes) ---
  activityTab: 'comments' | 'history' = 'comments';
  newComment = '';
  postingComment = false;
  composerExpanded = false;

  constructor(
    private orderService: OrderService,
    private fb: FormBuilder,
    private accountService: AccountService,
    private userService: UserService,
    private productService: ProductService,
    private authService: AuthService,
    private router: Router,
    private messageService: MessageService,
    private colPrefService: ColumnPreferenceService,
    private exportService: ExportExcelService,
  ) {
    this.currentRole = this.authService.getUserRole();
    this.currentUserId = this.authService.getUserDetails()?.userId;
  }

  ngOnInit(): void {
    const savedRows = localStorage.getItem('rowsPerPage');
    if (savedRows) {
      this.tableRows = +savedRows;
    }
    this.initColumns();
    this.fetchInitialData();
    this.loadAssignableUsers();
    this.searchAccountSubject.pipe(debounceTime(800)).subscribe(searchText => {
      this.searchAccountQuery = searchText;
      this.getAccounts();
    });

    // Same rule as the create wizard: Dirección only applies to Domicilio.
    this.viewForm.get('location')?.valueChanges.subscribe(location => {
      if (location !== OrderLocation.DOMICILIO) {
        this.viewForm.get('address')?.setValue('');
      }
    });

    this.itemsCatalogSearchSubject.pipe(debounceTime(500)).subscribe(term => {
      this.itemsCatalogSearchTerm = term;
      this.reloadItemsCatalog();
    });
  }

  get hasAccountAccess(): boolean {
    return this.authService.canAccessAction('orders', 'accountSelection');
  }

  get hasEditAccess(): boolean {
    return this.authService.canAccessAction('orders', 'edit');
  }

  get hasCreateAccess(): boolean {
    return this.authService.canAccessAction('orders', 'create');
  }

  // Completada/Cerrada are terminal — nobody edits a finished or closed order, BILD included.
  // Below that: BILD_* roles with order access can edit at CREADA or EN_PROCESO; CLIENTE_*
  // roles (also granted general edit access) can only edit while the order is still CREADA —
  // once it moves on, a client can no longer touch it and must go through Bild operations.
  // Enforced here for the UI affordances and again server-side in OrderService#updateOrder.
  get canEditFields(): boolean {
    if (!this.hasEditAccess || !this.viewingOrder) return false;
    const status = this.viewingOrder.status;
    if (status === OrderStatus.COMPLETADA || status === OrderStatus.CANCELADA) return false;
    if (this.authService.isBildUser()) return true;
    return status === OrderStatus.CREADA;
  }

  // Asignado is BILD-only, and — like every other field — locked once the order is
  // Completada/Cancelada.
  get canEditAssignee(): boolean {
    if (!this.hasEditAccess || !this.viewingOrder) return false;
    const status = this.viewingOrder.status;
    if (status === OrderStatus.COMPLETADA || status === OrderStatus.CANCELADA) return false;
    return this.authService.isBildUser();
  }

  // Row-level counterpart of canEditFields (evaluated per order in the list, not against
  // viewingOrder) — same access rule: BILD_* can cancel at CREADA/EN_PROCESO, CLIENTE_* only
  // at CREADA, nobody once the order is Completada/Cancelada.
  canCancelOrder(order: Order): boolean {
    if (!this.hasEditAccess) return false;
    if (order.status === OrderStatus.COMPLETADA || order.status === OrderStatus.CANCELADA) return false;
    if (this.authService.isBildUser()) return true;
    return order.status === OrderStatus.CREADA;
  }

  // While CREADA, the order hasn't started being worked yet, so edits are low-stakes and
  // save immediately (see confirmEditField/saveFieldImmediate) instead of requiring the
  // manual justification comment the batched flow demands once it leaves CREADA — at that
  // point a change has to go through Bild operations and may carry real warehouse
  // movement/cost, which is what that comment is meant to capture.
  get isCreada(): boolean {
    return this.viewingOrder?.status === OrderStatus.CREADA;
  }

  // The Cuenta column only earns its place when "Todas las cuentas" is selected — once a
  // single account is chosen (by a BILD role, or fixed for CLIENTE_*), every row already
  // shares that account, so the column would be redundant.
  get showAccountColumn(): boolean {
    return this.filterForm.get('accountId')?.value == null;
  }

  get availableColumns(): ColumnConfig[] {
    return this.ALL_COLUMNS.filter(c => c.field !== 'account.name' || this.showAccountColumn);
  }

  loadAssignableUsers() {
    this.userService.getAssignableUsers([
      RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_VENTAS
    ]).subscribe(users => this.assignableUsers = users);
  }

  fetchInitialData() {
    if (this.hasAccountAccess) {
      this.getAccounts(() => this.restoreLastAccount());
    } else {
      this.getAccount();
    }
  }

  loadAccounts(event: any) {
    this.searchAccountSubject.next(event.filter || '');
  }

  getAccounts(onComplete?: () => void) {
    this.accountLoading = true;
    this.accountService.searchAllAccounts(this.searchAccountQuery).subscribe((response: PageableAccount) => {
      this.accounts = [ALL_ACCOUNTS_OPTION, ...response.content];
      this.accountLoading = false;
      onComplete?.();
    });
  }

  getAccount() {
    this.filterForm.get('accountId')?.disable();
    this.accountLoading = true;
    this.userAccount = this.authService.getUserAccount();
    this.filterForm.patchValue({ accountId: this.userAccount.accountId });
    this.accountService.getDropdownAccount(this.userAccount.accountId).subscribe((response: AccountDropdown[]) => {
      this.accounts = response;
      this.accountLoading = false;
      this.reload();
    });
  }

  private get lastAccountStorageKey(): string {
    return `orders_last_account_${this.currentUserId}`;
  }

  private restoreLastAccount() {
    const raw = localStorage.getItem(this.lastAccountStorageKey);
    const lastAccountId = raw ? Number(raw) : null;
    if (!lastAccountId) {
      this.reload();
      return;
    }

    this.accountService.getDropdownAccount(lastAccountId).subscribe((response: AccountDropdown[]) => {
      const match = response?.[0];
      if (!match) {
        this.reload();
        return;
      }
      if (!this.accounts.some((a: any) => a.accountId === match.accountId)) {
        this.accounts = [ALL_ACCOUNTS_OPTION, match, ...this.accounts.slice(1)];
      }
      this.filterForm.patchValue({ accountId: match.accountId });
      this.onAccountChange(false);
    });
  }

  onAccountChange(persist: boolean = true) {
    const accountId = this.filterForm.get('accountId')?.value;
    if (persist) {
      if (accountId) {
        localStorage.setItem(this.lastAccountStorageKey, accountId.toString());
      } else {
        localStorage.removeItem(this.lastAccountStorageKey);
      }
    }
    // The Cuenta column's availability depends on the account filter, so column state
    // (toggle list + persisted prefs) needs recomputing whenever it changes.
    this.initColumns();
    this.firstPage = 0;
    this.reload();
  }

  reload() {
    this.loadOrders({ ...(this.lastLazyEvent ?? {}), first: 0, rows: this.tableRows });
  }

  private extractConstraints(filters: any, field: string): { value: any; matchMode?: string }[] {
    const meta = filters?.[field];
    if (!meta) return [];
    const list = Array.isArray(meta) ? meta : (meta.constraints ?? [meta]);
    return list.filter((c: any) => c?.value !== null && c?.value !== undefined && c?.value !== '');
  }

  private extractTextFilter(filters: any, field: string): string | undefined {
    const constraints = this.extractConstraints(filters, field);
    return constraints.length ? constraints[0].value : undefined;
  }

  private extractBooleanFilter(filters: any, field: string): boolean | undefined {
    const constraints = this.extractConstraints(filters, field);
    return constraints.length ? Boolean(constraints[0].value) : undefined;
  }

  private extractNumberFilterWithMode(filters: any, field: string): { value?: number; matchMode?: string } {
    const constraints = this.extractConstraints(filters, field);
    if (!constraints.length) return {};
    return { value: Number(constraints[0].value), matchMode: constraints[0].matchMode };
  }

  private extractEqualsNumber(filters: any, field: string): number | undefined {
    const constraints = this.extractConstraints(filters, field);
    return constraints.length ? Number(constraints[0].value) : undefined;
  }

  private extractDateRange(filters: any, field: string): { from?: string; to?: string } {
    const constraints = this.extractConstraints(filters, field);
    let from: Date | undefined;
    let to: Date | undefined;
    constraints.forEach(c => {
      if (c.matchMode === 'dateAfter' || c.matchMode === 'dateIs') from = c.value;
      if (c.matchMode === 'dateBefore' || c.matchMode === 'dateIs') to = c.value;
    });
    return {
      from: from ? from.toISOString() : undefined,
      to: to ? to.toISOString() : undefined,
    };
  }

  private buildSearchParams(event: any, overrides: Partial<OrderSearchParams> = {}): OrderSearchParams {
    const filters = event?.filters ?? {};
    const creationDateRange = this.extractDateRange(filters, 'creationDate');
    const updateDateRange = this.extractDateRange(filters, 'updateDate');
    const scheduledDateRange = this.extractDateRange(filters, 'scheduledDate');
    const completedDateRange = this.extractDateRange(filters, 'completedDate');
    const orderIdFilter = this.extractNumberFilterWithMode(filters, 'orderId');
    const productCountFilter = this.extractNumberFilterWithMode(filters, 'productCount');
    const totalArticlesFilter = this.extractNumberFilterWithMode(filters, 'totalArticles');
    const sortField = event?.sortField ?? 'orderId';
    const sortOrder = event?.sortOrder === 1 ? 'ASC' : 'DESC';

    return {
      accountId: this.filterForm.get('accountId')?.value ?? undefined,
      orderId: orderIdFilter.value,
      orderIdMatchMode: orderIdFilter.matchMode,
      description: this.extractTextFilter(filters, 'description'),
      accountName: this.showAccountColumn ? this.extractTextFilter(filters, 'account.name') : undefined,
      assignedUserId: this.extractEqualsNumber(filters, 'assignedUser.name'),
      status: this.extractTextFilter(filters, 'status'),
      orderType: this.extractTextFilter(filters, 'orderType'),
      platform: this.extractTextFilter(filters, 'platform'),
      verified: this.extractBooleanFilter(filters, 'verified'),
      productCount: productCountFilter.value,
      productCountMatchMode: productCountFilter.matchMode,
      totalArticles: totalArticlesFilter.value,
      totalArticlesMatchMode: totalArticlesFilter.matchMode,
      creationDateFrom: creationDateRange.from,
      creationDateTo: creationDateRange.to,
      updateDateFrom: updateDateRange.from,
      updateDateTo: updateDateRange.to,
      scheduledDateFrom: scheduledDateRange.from,
      scheduledDateTo: scheduledDateRange.to,
      completedDateFrom: completedDateRange.from,
      completedDateTo: completedDateRange.to,
      page: event?.first != null && event?.rows ? event.first / event.rows : 0,
      size: event?.rows ?? this.tableRows,
      sortBy: sortField,
      sortDirection: sortOrder,
      ...overrides,
    };
  }

  loadOrders(event: any) {
    this.loading = true;
    this.lastLazyEvent = event;
    const params = this.buildSearchParams(event);

    if (!params.accountId && !this.hasAccountAccess) {
      // A null accountId is a deliberate "Todas las cuentas" for BILD roles — only block
      // for CLIENTE_*, who must always have a fixed accountId; this just guards the brief
      // window before getAccount() resolves it.
      this.orders = [];
      this.totalRecords = 0;
      this.loading = false;
      return;
    }

    this.orderService.searchOrders(params).subscribe({
      next: (data) => {
        this.orders = data.content;
        this.totalRecords = data.totalElements;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  exportExcel() {
    const params = this.buildSearchParams(this.lastLazyEvent, { page: 0, size: this.totalRecords || this.tableRows });
    if (!this.totalRecords) {
      return;
    }

    this.exportLoading = true;
    this.orderService.searchOrders(params).subscribe({
      next: (data) => {
        this.exportService.export(data.content, this.appliedTableColumns, 'ordenes');
        this.exportLoading = false;
      },
      error: (error) => {
        this.exportLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo exportar las órdenes' });
      }
    });
  }

  openCreateOrder() {
    this.router.navigateByUrl('/home/order/create');
  }

  getStatusLabel(order: Order): string {
    return this.statusOptions.find(s => s.value === order.status)?.label ?? order.status ?? '';
  }

  getStatusSeverity(order: Order): 'info' | 'warn' | 'success' | 'secondary' | 'danger' {
    switch (order.status) {
      case OrderStatus.CREADA: return 'info';
      case OrderStatus.EN_PROCESO: return 'warn';
      case OrderStatus.COMPLETADA: return 'success';
      case OrderStatus.CANCELADA: return 'danger';
      default: return 'secondary';
    }
  }

  getStatusIcon(order: Order): string {
    switch (order.status) {
      case OrderStatus.CREADA: return 'pi pi-circle';
      case OrderStatus.EN_PROCESO: return 'pi pi-spin pi-spinner';
      case OrderStatus.COMPLETADA: return 'pi pi-check-circle';
      case OrderStatus.CANCELADA: return 'pi pi-ban';
      default: return 'pi pi-circle';
    }
  }

  getOrderTypeLabel(order: Order): string {
    return this.orderTypeOptions.find(o => o.value === order.orderType)?.label ?? order.orderType ?? '';
  }

  getOrderTypeSeverity(order: Order): 'info' | 'success' {
    return order.orderType === OrderType.ENTREGA ? 'info' : 'success';
  }

  // Código legible (ej. ENT-1045) para identificar el tipo de orden de un vistazo, sin
  // entrar al detalle — el ID real de BD sigue siendo order.orderId.
  getOrderCode(order: Order): string {
    if (!order.orderId) return '';
    const prefix = order.orderType ? this.ORDER_TYPE_ABBREVIATIONS[order.orderType] : undefined;
    return prefix ? `${prefix}-${order.orderId}` : String(order.orderId);
  }

  onOrderIdFilterChange(value: string, filterCallback: (value: unknown) => void): void {
    const digits = value.match(/\d+/g)?.join('');
    filterCallback(digits ? Number(digits) : null);
  }

  // Verificada reflects whether every item expected by the order has been scanned through
  // the (future) order-driven Agregar Articulos/Salida de Articulos flows — not computed yet
  // since those flows don't exist, so this just renders whatever the backend defaults to.
  getVerifiedLabel(order: Order): string {
    return order.verified ? 'Verificada' : 'Pendiente';
  }

  getVerifiedSeverity(order: Order): 'success' | 'secondary' {
    return order.verified ? 'success' : 'secondary';
  }

  getOrderTypeIcon(order: Order): string {
    return order.orderType === OrderType.ENTREGA ? 'pi pi-arrow-up-right' : 'pi pi-arrow-down-left';
  }

  getPlatformLogo(platform?: string): string | undefined {
    return PLATFORM_LOGOS[platform ?? ''];
  }

  //--- Column visibility toggle ---

  initColumns() {
    const columns = this.availableColumns;
    const allFields = columns.map(c => c.field);
    const saved = this.colPrefService.load(this.currentUserId, 'Orders');

    this.columnsToggleList = [...columns];

    const activeFields = saved
      ? this.colPrefService.reconcile(saved, allFields)
      : allFields.filter(f => {
        const col = columns.find(c => c.field === f);
        return col && !col.defaultHidden;
      });

    this.columnsToggleList.forEach(c => {
      c.visible = c.alwaysVisible ? true : activeFields.includes(c.field);
    });

    this.applyColumns(activeFields);
  }

  applyColumns(fields: string[]) {
    const alwaysVisible = this.columnsToggleList.filter(c => c.alwaysVisible).map(c => c.field);
    const merged = [...new Set([...alwaysVisible, ...fields])];
    this.appliedTableColumns = this.columnsToggleList.filter(c => merged.includes(c.field));
  }

  onSaveColumns() {
    const selected = this.columnsToggleList.filter(c => c.visible || c.alwaysVisible).map(c => c.field);
    this.colPrefService.save(this.currentUserId, 'Orders', selected);
    this.applyColumns(selected);
  }

  onClearColumns() {
    this.columnsToggleList.forEach(c => c.visible = c.alwaysVisible ?? false);
    this.selectAll = false;
    this.applyColumns([]);
  }

  get filteredColumns(): ColumnConfig[] {
    if (!this.columnSearchTerm.trim()) return this.columnsToggleList;
    return this.columnsToggleList.filter(c =>
      c.header.toLowerCase().includes(this.columnSearchTerm.toLowerCase())
    );
  }

  onSelectAll(event: any) {
    this.columnsToggleList.forEach(c => {
      if (!c.alwaysVisible) c.visible = event.checked;
    });
  }

  onColumnToggle() {
    const toggleable = this.columnsToggleList.filter(c => !c.alwaysVisible);
    this.selectAll = toggleable.every(c => c.visible);
  }

  resolveField(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }

  //--- View details dialog (+ inline edit) ---

  openViewDetails(order: Order) {
    if (!order.orderId) return;
    this.viewingOrder = null;
    this.movements = [];
    this.editingField = null;
    this.saveComment = '';
    this.newComment = '';
    this.composerExpanded = false;
    this.activityTab = 'comments';
    this.detailsExpanded = true;
    this.editingItems = false;
    this.itemsStockWarning = null;
    this.viewLoading = true;
    this.viewDialogVisible = true;

    forkJoin({
      order: this.orderService.getOrder(order.orderId),
      movements: this.orderService.getOrderMovements(order.orderId),
    }).subscribe({
      next: ({ order: fullOrder, movements }) => {
        this.viewingOrder = fullOrder;
        this.movements = movements.map(m => ({
          ...m,
          changeDetail: typeof m.changeDetail === 'string' ? JSON.parse(m.changeDetail) : m.changeDetail
        }));
        this.viewOriginal = {
          description: fullOrder.description ?? '',
          scheduledDate: this.toDate(fullOrder.scheduledDate),
          titular: fullOrder.titular ?? '',
          location: fullOrder.location ?? null,
          address: fullOrder.address ?? '',
          platform: fullOrder.platform ?? null,
          assignedUserId: fullOrder.assignedUser?.userId ?? null,
        };
        this.viewForm.reset();
        this.viewForm.patchValue(this.viewOriginal);
        this.originalItems = this.toItemDrafts(fullOrder.items ?? []);
        this.itemsDraft = this.toItemDrafts(fullOrder.items ?? []);
        this.viewLoading = false;
      },
      error: () => {
        this.viewLoading = false;
        this.viewDialogVisible = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el detalle de la orden' });
      }
    });
  }

  //--- Cancelar orden ---

  openCancelDialog(order: Order) {
    if (!this.canCancelOrder(order)) return;
    this.cancelingOrder = order;
    this.cancelComment = '';
    this.cancelDialogVisible = true;
  }

  closeCancelDialog() {
    this.cancelDialogVisible = false;
    this.cancelingOrder = null;
    this.cancelComment = '';
  }

  confirmCancelOrder() {
    if (!this.cancelingOrder?.orderId || !this.cancelComment.trim()) return;
    const orderId = this.cancelingOrder.orderId;

    this.cancelingOrderLoading = true;
    this.orderService.cancelOrder(orderId, this.cancelComment).subscribe({
      next: () => {
        this.cancelingOrderLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Exitoso', detail: 'Orden cancelada correctamente', life: 5000 });
        this.closeCancelDialog();
        this.reload();
        if (this.viewingOrder?.orderId === orderId) {
          this.openViewDetails(this.viewingOrder);
        }
      },
      error: (error) => {
        this.cancelingOrderLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo cancelar la orden' });
      }
    });
  }

  private toDate(value: any): Date | null {
    if (!value) return null;
    return value instanceof Date ? value : new Date(value);
  }

  // Click-to-edit, Jira-style: only one field editable at a time. Opening another field
  // reverts any field left mid-edit without being confirmed via its check icon.
  startEditField(field: string) {
    const allowed = field === 'assignedUserId' ? this.canEditAssignee : this.canEditFields;
    if (!allowed) return;
    if (this.editingField && this.editingField !== field) {
      this.cancelEditField(this.editingField);
    }
    if (this.editingItems) {
      this.cancelEditItems();
    }
    this.editingField = field;
  }

  confirmEditField(field: string) {
    if (field === 'assignedUserId') {
      this.saveAssignment();
      return;
    }
    if (this.isCreada) {
      this.saveFieldImmediate(field);
      return;
    }
    this.editingField = null;
  }

  assignToMe() {
    if (!this.canEditAssignee) return;
    const userId = Number(this.authService.getUserDetails()?.userId);
    this.viewForm.get('assignedUserId')?.setValue(userId);
    this.saveAssignment();
  }

  cancelEditField(field: string) {
    this.viewForm.get(field)?.setValue(this.viewOriginal[field]);
    this.editingField = null;
  }

  // Asignado is a Jira-style quick action (like "Assign to me") rather than a metadata edit:
  // it saves immediately with an auto-generated comment instead of joining the batched
  // "Tienes cambios sin guardar" flow the other fields use, so it never shows that bar.
  saveAssignment() {
    if (!this.viewingOrder?.orderId) return;
    const assignedId = this.viewForm.get('assignedUserId')?.value ?? null;
    const assignee = this.assignableUsers.find(u => u.userId === assignedId);
    const comment = assignedId
      ? `Orden asignada a ${assignee?.name ?? ''} ${assignee?.lastname ?? ''}`.trim()
      : 'Orden sin asignar';

    this.savingAssignment = true;
    this.orderService.updateOrder(this.viewingOrder.orderId, {
      description: this.viewOriginal['description'] || undefined,
      scheduledDate: this.viewOriginal['scheduledDate'],
      titular: this.viewOriginal['titular'] || undefined,
      location: this.viewOriginal['location'],
      address: this.viewOriginal['location'] === OrderLocation.DOMICILIO ? this.viewOriginal['address'] : undefined,
      platform: this.viewOriginal['platform'],
      assignedUserId: assignedId,
      comment,
    }).subscribe({
      next: () => {
        this.savingAssignment = false;
        this.editingField = null;
        this.viewOriginal['assignedUserId'] = assignedId;
        this.viewingOrder!.assignedUser = assignee as any;
        this.orderService.getOrderMovements(this.viewingOrder!.orderId!).subscribe(movements => {
          this.movements = movements.map(m => ({
            ...m,
            changeDetail: typeof m.changeDetail === 'string' ? JSON.parse(m.changeDetail) : m.changeDetail
          }));
        });
        this.reload();
        this.messageService.add({ severity: 'success', summary: 'Exitoso', detail: 'Asignación actualizada', life: 4000 });
      },
      error: (error) => {
        this.savingAssignment = false;
        this.viewForm.get('assignedUserId')?.setValue(this.viewOriginal['assignedUserId']);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo actualizar la asignación' });
      }
    });
  }

  // Same immediate-save pattern as saveAssignment(), but for the remaining metadata fields —
  // only reachable while isCreada is true (see confirmEditField). The comment is auto-generated
  // since nothing needs justifying yet; once the order leaves CREADA this path isn't used and
  // edits fall back to the batched flow that requires a real, user-written comment.
  saveFieldImmediate(field: string) {
    if (!this.viewingOrder?.orderId) return;
    const value = this.viewForm.value;

    this.savingField = true;
    this.orderService.updateOrder(this.viewingOrder.orderId, {
      description: value.description || undefined,
      scheduledDate: value.scheduledDate,
      titular: value.titular || undefined,
      location: value.location,
      address: value.location === OrderLocation.DOMICILIO ? value.address : undefined,
      platform: value.platform,
      assignedUserId: this.viewOriginal['assignedUserId'],
      comment: `Actualización de ${this.getFieldLabel(field)} (orden en Creada)`,
    }).subscribe({
      next: () => {
        this.savingField = false;
        this.editingField = null;
        this.viewOriginal[field] = value[field];
        if (field === 'location') {
          this.viewOriginal['address'] = value.address;
        }
        this.orderService.getOrderMovements(this.viewingOrder!.orderId!).subscribe(movements => {
          this.movements = movements.map(m => ({
            ...m,
            changeDetail: typeof m.changeDetail === 'string' ? JSON.parse(m.changeDetail) : m.changeDetail
          }));
        });
        this.reload();
      },
      error: (error) => {
        this.savingField = false;
        this.viewForm.get(field)?.setValue(this.viewOriginal[field]);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo actualizar la orden' });
      }
    });
  }

  //--- Productos: editable list, same role/status gate as the other fields (canEditFields) ---

  private toItemDrafts(items: OrderItem[]): OrderItemDraft[] {
    return items.map(i => ({
      productId: i.productId,
      sku: i.sku,
      description: i.description,
      variant: i.variant,
      uom: i.uom,
      availableQuantity: 0,
      quantity: i.quantity,
      comments: i.comments ?? '',
    }));
  }

  // Order-independent comparison of productId/quantity/comments against the last-saved list —
  // drives both the pending-changes bar (once past CREADA) and whether confirmEditItems has
  // anything worth re-validating.
  get itemsChanged(): boolean {
    const normalize = (items: OrderItemDraft[]) => items
      .map(i => `${i.productId}:${i.quantity}:${i.comments ?? ''}`)
      .sort()
      .join('|');
    return normalize(this.itemsDraft) !== normalize(this.originalItems);
  }

  get itemsTotalQuantity(): number {
    return this.itemsDraft.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  startEditItems() {
    if (!this.canEditFields) return;
    if (this.editingField) {
      this.cancelEditField(this.editingField);
    }
    this.itemsDraft = this.originalItems.map(i => ({ ...i }));
    this.itemsStockWarning = null;
    this.editingItems = true;
    this.reloadItemsCatalog();
    this.refreshItemsAvailability();
  }

  // Fills in "En Stock" for every line already in the draft, excluding this order's own
  // reservation so the cap reflects the true ceiling (stock + what this order already holds),
  // matching the save-time validation in confirmEditItems.
  private refreshItemsAvailability() {
    if (!this.viewingOrder?.orderId || !this.itemsDraft.length) return;
    const productIds = this.itemsDraft.map(i => i.productId);
    this.orderService.getAvailableQuantities(productIds, this.viewingOrder.orderId).subscribe(available => {
      this.itemsDraft.forEach(item => {
        item.availableQuantity = available[item.productId] ?? 0;
      });
    });
  }

  cancelEditItems() {
    this.itemsDraft = this.originalItems.map(i => ({ ...i }));
    this.itemsStockWarning = null;
    this.editingItems = false;
  }

  // Re-validates against fresh stock (excluding this order's own current reservation) before
  // doing anything else — mirrors the create wizard's confirmOrder(). Clean -> saves
  // immediately at CREADA, or just closes the editor so the change joins the batched
  // mandatory-comment flow once the order has moved past CREADA.
  confirmEditItems() {
    if (!this.viewingOrder?.orderId || !this.viewingOrder.account?.accountId) return;
    if (!this.itemsDraft.length) {
      this.messageService.add({ severity: 'warn', summary: 'Sin productos', detail: 'La orden debe tener al menos un producto.' });
      return;
    }

    this.savingItems = true;
    this.orderService.validateStock({
      accountId: this.viewingOrder.account.accountId,
      orderType: this.viewingOrder.orderType!,
      items: this.itemsDraft.map(i => ({ productId: i.productId, quantity: i.quantity })),
      excludeOrderId: this.viewingOrder.orderId,
    }).subscribe({
      next: (violations) => {
        this.savingItems = false;
        if (violations.length) {
          this.applyItemViolations(violations,
            'Algunos productos ya no tienen suficiente stock disponible. Ajusta las cantidades marcadas en rojo.');
          return;
        }
        if (this.isCreada) {
          this.saveItemsImmediate();
        } else {
          this.editingItems = false;
        }
      },
      error: () => {
        this.savingItems = false;
      }
    });
  }

  private applyItemViolations(violations: StockViolation[], message: string) {
    const byProduct = new Map(violations.map(v => [v.productId, v]));
    this.itemsDraft.forEach(item => {
      const violation = byProduct.get(item.productId);
      item.insufficientStock = !!violation;
      if (violation) {
        item.availableQuantity = violation.availableQuantity;
      }
    });
    this.itemsStockWarning = message;
    this.editingItems = true;
  }

  // Same immediate-save pattern as saveFieldImmediate(), but for the items list — only
  // reached while isCreada is true (see confirmEditItems). Uses updateOrder's response
  // directly to refresh in place rather than a full dialog reopen, so any other field a user
  // might have mid-typed elsewhere isn't disturbed.
  saveItemsImmediate() {
    if (!this.viewingOrder?.orderId) return;
    const items = this.itemsDraft.map(i => ({
      productId: i.productId,
      quantity: i.quantity,
      comments: i.comments || undefined,
    }));

    this.savingItems = true;
    this.orderService.updateOrder(this.viewingOrder.orderId, {
      description: this.viewOriginal['description'] || undefined,
      scheduledDate: this.viewOriginal['scheduledDate'],
      titular: this.viewOriginal['titular'] || undefined,
      location: this.viewOriginal['location'],
      address: this.viewOriginal['location'] === OrderLocation.DOMICILIO ? this.viewOriginal['address'] : undefined,
      platform: this.viewOriginal['platform'],
      assignedUserId: this.viewOriginal['assignedUserId'],
      items,
      comment: 'Actualización de Productos (orden en Creada)',
    }).subscribe({
      next: (updated) => {
        this.savingItems = false;
        this.editingItems = false;
        this.itemsStockWarning = null;
        this.viewingOrder!.items = updated.items;
        this.viewingOrder!.productCount = updated.productCount;
        this.viewingOrder!.totalArticles = updated.totalArticles;
        this.originalItems = this.toItemDrafts(updated.items ?? []);
        this.itemsDraft = this.toItemDrafts(updated.items ?? []);
        this.orderService.getOrderMovements(this.viewingOrder!.orderId!).subscribe(movements => {
          this.movements = movements.map(m => ({
            ...m,
            changeDetail: typeof m.changeDetail === 'string' ? JSON.parse(m.changeDetail) : m.changeDetail
          }));
        });
        this.reload();
      },
      error: (error) => {
        this.savingItems = false;
        const violations: StockViolation[] | undefined = error?.error?.violations;
        if (violations?.length) {
          this.applyItemViolations(violations, error?.error?.message ?? 'Algunos productos ya no tienen suficiente stock disponible.');
          return;
        }
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo actualizar la orden' });
      }
    });
  }

  //--- Productos: account-scoped catalog browser used to add new lines while editing ---

  onItemsCatalogSearch(term: string) {
    this.itemsCatalogSearchSubject.next(term);
  }

  reloadItemsCatalog() {
    this.loadItemsCatalog({ ...(this.lastItemsCatalogEvent ?? {}), first: 0, rows: this.itemsCatalogRows });
  }

  loadItemsCatalog(event: any) {
    const accountId = this.viewingOrder?.account?.accountId;
    this.lastItemsCatalogEvent = event;
    if (!accountId) {
      this.itemsCatalogProducts = [];
      this.itemsCatalogTotalRecords = 0;
      return;
    }

    this.itemsCatalogLoading = true;
    const params: ProductSearchParams = {
      accountId,
      generalSearch: this.itemsCatalogSearchTerm || undefined,
      page: event?.first != null && event?.rows ? event.first / event.rows : 0,
      size: event?.rows ?? this.itemsCatalogRows,
      sortBy: 'productId',
      sortDirection: 'ASC',
    };

    this.productService.searchProducts(params).subscribe({
      next: (data) => {
        this.itemsCatalogProducts = data.content;
        this.itemsCatalogTotalRecords = data.totalElements;
        this.itemsCatalogLoading = false;
      },
      error: () => {
        this.itemsCatalogLoading = false;
      }
    });
  }

  isItemInCart(productId: number): boolean {
    return this.itemsDraft.some(i => i.productId === productId);
  }

  canAddItemToCart(product: Product): boolean {
    if (this.isItemInCart(product.productId)) return false;
    if (this.viewingOrder?.orderType === OrderType.ENTREGA && (product.availableQuantity ?? 0) <= 0) return false;
    return true;
  }

  addItemToCart(product: Product) {
    if (!this.canAddItemToCart(product)) return;
    this.itemsDraft = [...this.itemsDraft, {
      productId: product.productId,
      sku: product.sku,
      description: product.description,
      variant: product.variant,
      uom: product.uom,
      availableQuantity: product.availableQuantity ?? 0,
      quantity: 1,
      comments: '',
    }];
  }

  removeItemFromCart(item: OrderItemDraft) {
    this.itemsDraft = this.itemsDraft.filter(i => i !== item);
  }

  openItemCommentPopover(event: Event, item: OrderItemDraft, popover: any) {
    this.activeItemCommentItem = item;
    popover.toggle(event);
  }

  // Read-only counterpart for the Productos table outside edit mode — same icon-triggered
  // popover pattern, just displaying the note instead of an editable p-editor.
  openViewCommentPopover(event: Event, item: OrderItem, popover: any) {
    this.activeViewCommentItem = item;
    popover.toggle(event);
  }

  private valuesEqual(a: any, b: any): boolean {
    if (a instanceof Date || b instanceof Date) {
      return new Date(a ?? 0).getTime() === new Date(b ?? 0).getTime();
    }
    return (a ?? '') === (b ?? '');
  }

  get hasPendingChanges(): boolean {
    // assignedUserId always saves immediately via saveAssignment(); while the order is
    // CREADA every other field does too (saveFieldImmediate/saveItemsImmediate) — none of
    // those ever join this batched comment-gated flow, which only applies once the order has
    // left CREADA.
    if (this.isCreada) return false;
    const value = this.viewForm.value;
    return this.itemsChanged || Object.keys(this.viewOriginal)
      .filter(key => key !== 'assignedUserId')
      .some(key => !this.valuesEqual(value[key], this.viewOriginal[key]));
  }

  discardViewChanges() {
    this.viewForm.reset();
    this.viewForm.patchValue(this.viewOriginal);
    this.editingField = null;
    this.saveComment = '';
    this.itemsDraft = this.originalItems.map(i => ({ ...i }));
    this.editingItems = false;
    this.itemsStockWarning = null;
  }

  saveViewChanges() {
    if (!this.viewingOrder?.orderId || !this.saveComment.trim()) return;
    const value = this.viewForm.value;
    this.savingChanges = true;

    this.orderService.updateOrder(this.viewingOrder.orderId, {
      description: value.description || undefined,
      scheduledDate: value.scheduledDate,
      titular: value.titular || undefined,
      location: value.location,
      address: value.location === OrderLocation.DOMICILIO ? value.address : undefined,
      platform: value.platform,
      assignedUserId: value.assignedUserId,
      items: this.itemsChanged ? this.itemsDraft.map(i => ({
        productId: i.productId,
        quantity: i.quantity,
        comments: i.comments || undefined,
      })) : undefined,
      comment: this.saveComment,
    }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Exitoso', detail: 'Orden actualizada correctamente', life: 5000 });
        this.savingChanges = false;
        this.reload();
        this.openViewDetails(this.viewingOrder!);
      },
      error: (error) => {
        this.savingChanges = false;
        const violations: StockViolation[] | undefined = error?.error?.violations;
        if (violations?.length) {
          this.applyItemViolations(violations, error?.error?.message ?? 'Algunos productos ya no tienen suficiente stock disponible.');
          return;
        }
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo actualizar la orden' });
      }
    });
  }

  //--- Activity: Comentarios / Historial ---

  get commentMovements(): OrderMovement[] {
    return this.movements.filter(m => m.movementType === 'COMMENT');
  }

  get historyMovements(): OrderMovement[] {
    return this.movements.filter(m => m.movementType !== 'COMMENT');
  }

  get currentUserInitials(): string {
    const user = this.authService.getUserDetails();
    return this.getInitials(user?.name, user?.lastname);
  }

  getInitials(name?: string, lastname?: string): string {
    const initials = `${name?.charAt(0) ?? ''}${lastname?.charAt(0) ?? ''}`;
    return initials.toUpperCase() || '?';
  }

  get assignableUserOptions(): { userId?: number; fullName: string }[] {
    return this.assignableUsers.map(u => ({ userId: u.userId, fullName: `${u.name ?? ''} ${u.lastname ?? ''}`.trim() }));
  }

  // Reads the staged (possibly just-edited) assignedUserId from viewForm rather than the
  // order's originally-loaded assignedUser, so a confirmed-but-not-yet-saved change shows
  // immediately — same pattern as every other inline-editable field here. Falls back to the
  // order's own assignedUser if that id isn't in assignableUsers (e.g. no pending edit, just
  // someone outside the current assignable set).
  getAssignedUserDisplay(): User | undefined {
    const userId = this.viewForm.get('assignedUserId')?.value;
    if (!userId) return undefined;
    const fromList = this.assignableUsers.find(u => u.userId === userId);
    if (fromList) return fromList;
    const orderAssignee = this.viewingOrder?.assignedUser;
    return orderAssignee?.userId === userId ? orderAssignee : undefined;
  }

  cancelComposer() {
    this.newComment = '';
    this.composerExpanded = false;
  }

  submitComment() {
    if (!this.viewingOrder?.orderId || !this.newComment.trim()) return;
    this.postingComment = true;
    this.orderService.addComment(this.viewingOrder.orderId, this.newComment).subscribe({
      next: (movements) => {
        this.movements = movements.map(m => ({
          ...m,
          changeDetail: typeof m.changeDetail === 'string' ? JSON.parse(m.changeDetail) : m.changeDetail
        }));
        this.newComment = '';
        this.composerExpanded = false;
        this.postingComment = false;
      },
      error: (error) => {
        this.postingComment = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo agregar el comentario' });
      }
    });
  }

  getFieldLabel(key: string): string {
    const labels: Record<string, string> = {
      description: 'Descripción',
      assignedUser: 'Asignado',
      scheduledDate: 'Fecha Programada',
      titular: 'Titular',
      location: 'Ubicación',
      address: 'Dirección',
      platform: 'Plataforma',
      items: 'Productos',
      status: 'Estado',
      closeReason: 'Motivo de cancelación',
    };
    return labels[key] || key;
  }

  getFieldValue(value: any): string {
    return value ?? 'null';
  }
}
